import { Component, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { EmployeeService } from '../services/employee.service';
import { OverlayPanel } from 'primeng/overlaypanel';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit {
  permissions: string[] = []; 
  currentDate: string = '';
  userData: any = null;
  fullname: string = '';
  employeeData: any = null;
  notificationCount: number = 0;
  notifications: any[] = [];
  isMenuCollapsed: boolean = false;
  isLoading: boolean = false;

  @ViewChild('op') op!: OverlayPanel;

  menuOptions = [
    {
      label: 'Inicio',
      icon: 'pi pi-home',
      route: '/dashboard/home',
      permission: 'VIEW_INICIO_ADMIN',
    },
    {
      label: 'Administración',
      icon: 'pi pi-warehouse',
      route: '/dashboard/management',
      permission: 'VIEW_ADMINISTRACION_ADMIN', 
    },
    {
      label: 'Boletas',
      icon: 'pi pi-file',
      route: '/dashboard/payment-slips',
      permission: 'VIEW_BOLETAS_ADMIN',
    },
    {
      label: 'Solicitudes',
      icon: 'pi pi-clock',
      route: '/dashboard/solicitude',
      permission: 'VIEW_SOLICITUDES_ADMIN',
    },
    {
      label: 'Ticket y Vales',
      icon: 'pi pi-ticket',
      route: '/dashboard/tickets',
      permission: 'VIEW_TICKETS_ADMIN',
    },
    {
      label: 'Vacaciones',
      icon: 'pi pi-sun',
      route: '/dashboard/vacation',
      permission: 'VIEW_VACACIONES_ADMIN', 
    },
    {
      label: 'Empleados',
      icon: 'pi pi-user',
      permission: 'VIEW_EMPLEADOS_ADMIN',
      expanded: false,
      children: [
        {
          label: 'Empleados activos',
          route: '/dashboard/employee/list',
          permission: 'VIEW_EMPLEADOS_ACTIVOS_ADMIN',
        },
        {
          label: 'Cesados',
          route: '/dashboard/employee/cesados',
          permission: 'VIEW_EMPLEADOS_CESADOS_ADMIN',
        },
        {
          label: 'Contrato/ Renov.',
          route: '/dashboard/employee/contract',
          permission: 'VIEW_CONTRATOS_RENOVACIONES_ADMIN',
        },
      ]
    }
  ];

  filteredMenuOptions: any[] = [];

  constructor(
    private router: Router,
    private employeeService: EmployeeService
  ) {}

  ngOnInit(): void {
    const userDataString = localStorage.getItem('access');
    if (userDataString) {
      this.userData = JSON.parse(userDataString);
      this.getName();
    }

    this.loadPermissions();
    this.filterMenuOptions();

    this.openMenuByRoute();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.openMenuByRoute();
      });

    const date = new Date();
    const months = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ];

    // Construir la fecha en el formato deseado
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    // Formato: "Diciembre 18 del 2024"
    this.currentDate = `${month} ${day} del ${year}`;

    this.getAllEmployeesToRenewContract();
  }

  openMenuByRoute(): void {
    const currentUrl = this.router.url;

    this.filteredMenuOptions.forEach(option => {
      if (option.children) {
        const hasActiveChild = option.children.some((sub: any) =>
          currentUrl.includes(sub.route)
        );

        option.expanded = hasActiveChild;
      }
    });
  }

  isParentActive(option: any): boolean {
    if (!option.children) return false;

    if (option.expanded) return false;

    return option.children.some((sub: any) =>
      this.router.url.includes(sub.route)
    );
  }

  onParentClick(option: any): void {
    if (this.isMenuCollapsed) {
      this.isMenuCollapsed = false;
    }

    this.filteredMenuOptions.forEach(item => {
      if (item !== option && item.children) {
        item.expanded = false;
      }
    });

    option.expanded = !option.expanded;
  }

  toggleMenu(): void {
    this.isMenuCollapsed = !this.isMenuCollapsed;

    if (this.isMenuCollapsed) {
      this.filteredMenuOptions.forEach(option => {
        if (option.children) {
          option.expanded = false;
        }
      });
    }
  }

  getAllEmployeesToRenewContract(): void {
    this.employeeService.getEmployeesToRenewContract().subscribe({
      next: (data: any[]) => {
        if (data && data.length > 0) {
          this.processContractNotifications(data);
        } else {
          this.clearContractNotifications();
        }
      },
      error: (err) => {
        console.error('Error al obtener el listado:', err);
        this.clearContractNotifications();
      },
    });
  }

  clearContractNotifications(): void {
    this.notifications = this.notifications.filter(
      (n) => !n.isContractNotification
    );
    this.notificationCount = this.notifications.length;
  }

  processContractNotifications(employees: any[]): void {
    // Crear notificaciones basadas en los empleados
    if (employees.length === 1) {
      const employee = employees[0];
      this.notifications = [
        {
          title: 'Contratos por vencer',
          message: `El contrato de ${employee.employeeName} está por vencer el día ${employee.contractEndDate}.`,
          isContractNotification: true,
        },
      ];
    } else {
      this.notifications = [
        {
          title: 'Contratos por vencer',
          message: `Tienes ${employees.length} empleados con contratos por vencer. <a href="/dashboard/employee/list" class="see-more-link">Ver más</a>`,
          isContractNotification: true,
          employees: employees,
        },
      ];
    }

    this.notificationCount = this.notifications.length;
  }

  loadPermissions(): void {
    const storedAccess = localStorage.getItem('access');
    if (storedAccess) {
      const parsedAccess = JSON.parse(storedAccess);
      this.permissions = parsedAccess.permissions || [];
    }
  }

  filterMenuOptions(): void {
    this.filteredMenuOptions = this.menuOptions
      .map((option: any) => {
        if (!this.permissions.includes(option.permission)) {
          return null;
        }

        if (option.children) {
          const allowedChildren = option.children.filter((child: any) =>
            this.permissions.includes(child.permission)
          );

          if (allowedChildren.length === 0) {
            return null;
          }

          return {
            ...option,
            children: allowedChildren,
          };
        }

        return option;
      })
      .filter(Boolean);
  }

  getName(): void {
    this.isLoading = true;
    this.employeeService
      .getAuthenticatedUser(this.userData.employeeId)
      .subscribe(
        (employee) => {
          if (employee) {
            this.employeeData = employee;
            this.fullname =
              employee.names.split(' ')[0] +
              ' ' +
              employee.lastNames.split(' ')[0];
            this.isLoading = false;
          } else {
            console.log('No se encontró el empleado');
            this.isLoading = false;
          }
        },
        (error) => {
          console.error('Error al obtener el empleado:', error);
          this.isLoading = false;
        }
      );
  }

  toggleNotification(event: Event, button: HTMLElement) {
    this.op.toggle(event, button);

    setTimeout(() => {
      const panelElement = document.querySelector(
        '.p-overlaypanel'
      ) as HTMLElement;
      const button = document.querySelector('.pi-bell')?.parentElement;

      if (panelElement && button) {
        const navbar = document.querySelector('.navbar') as HTMLElement;
        const buttonRect = button.getBoundingClientRect();

        const navbarRect = navbar?.getBoundingClientRect();
        let offsetLeft = navbarRect
          ? buttonRect.left - navbarRect.left
          : buttonRect.left;

        if (this.isMenuCollapsed) {
          offsetLeft -= 180;
        }

        panelElement.style.left = `${offsetLeft - 15}px`;
        panelElement.style.top = `${buttonRect.bottom - 5}px`;
        panelElement.style.transform = `translateX(-50%)`;
      }
    }, 0);
  }

  logout(): void {
    localStorage.clear();
    this.router.navigate(['/auth/login']);
  }

  updateNotifications(): void {
    this.notifications = [];

    this.notificationCount = this.notifications.length;
  }
}
